import { useMemo, useState } from 'react';

import { Column, Row } from 'react-table';

import { LinkButton } from 'components';
import { StaticTable } from 'components/StaticTable';
import { convertToDateShort } from 'helpers';
import { useCandidateContext } from 'hooks';
import {
  CandidateApplicationProps,
  EmployerTableGlobalFilterOptions,
} from 'types';

import { DashboardWrapper, CellWrapper } from '../styledComponents';
import { EmployerTableSidebar } from './EmployerTableSidebar';

export const EmployerTable = () => {
  const { candidatesLoaded, candidateArray } = useCandidateContext();
  const [candidateFilter, setCandidateFilter] =
    useState<EmployerTableGlobalFilterOptions>('unchecked');

  const backgroundColor = (verification: string) => {
    switch (verification) {
      case 'true':
        return '#f2fde4';
      case 'false':
        return '#fbe2f0';
      case 'not_sure':
        return '#fff2df';
      default:
        // a non-empty string will override default css behavior
        return '';
    }
  };
  const data = useMemo(
    () =>
      candidateArray.filter((candidate) => {
        const { review_status } = candidate;
        let result = true;
        switch (candidateFilter) {
          case 'all':
            result = true;
            break;
          case 'unchecked':
            result = review_status === 'pending_user_review';
            break;
          case 'inProgress':
            result = review_status === 'review_in_progress';
            break;
          case 'didNotWorkForUs':
            result = review_status === 'did_not_work_certain';
            break;
          case 'insufficientData':
            result = review_status === 'did_not_work_insufficient_info';
            break;
          case 'verified':
            result = review_status === 'reviewed_and_verified';
            break;
          default:
            result = true;
            break;
        }
        return result;
      }),
    [candidateArray, candidateFilter],
  );

  const columns = useMemo(
    (): Array<Column<CandidateApplicationProps>> => [
      {
        id: 'edit',
        Header: () => {
          return (
            <CellWrapper aria-label='Edit'>
              <p />
            </CellWrapper>
          );
        },
        Cell: (row: any) => {
          return (
            <CellWrapper>
              <LinkButton
                variant='outlined'
                sx={{ width: '70%' }}
                to={`candidate/${row.row.original.id}`}
              >
                EDIT
              </LinkButton>
            </CellWrapper>
          );
        },
      },
      {
        id: 'firstname',
        Header: 'Given Name',
        accessor: 'firstname',
      },
      {
        id: 'lastname',
        Header: 'Family Name',
        accessor: 'lastname',
      },
      {
        id: 'date_of_birth',
        Header: 'DOB',
        accessor: (row) => convertToDateShort(row.birthdate),
      },
      {
        id: 'passport_number',
        Header: 'Passport',
        accessor: 'passport_number',
      },
      {
        id: 'tazkera',
        Header: 'Tazkera',
        accessor: 'tazkera',
      },
      {
        id: 'case_number',
        Header: 'SIV ID',
        accessor: 'case_number',
      },
    ],
    [],
  );

  const globalFiltersApplied = useMemo(() => {
    return candidateFilter !== 'all';
  }, [candidateFilter]);

  return (
    <DashboardWrapper>
      <>
        <EmployerTableSidebar
          setFilter={setCandidateFilter}
          filter={candidateFilter}
          globalFiltersApplied={globalFiltersApplied}
        />

        <StaticTable<CandidateApplicationProps>
          loading={!candidatesLoaded}
          globalFiltersApplied={globalFiltersApplied}
          handleGlobalFilterClear={() => setCandidateFilter('all')}
          useTableOptions={{
            data: data,
            columns: columns,
            getRowProps: (row: Row) => ({
              style: {
                background: backgroundColor(row.values.verification),
              },
            }),
          }}
        />
      </>
    </DashboardWrapper>
  );
};
